import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TouchEvent as ReactTouchEvent } from 'react';

import { SLIDES } from './content';

const SWIPE_THRESHOLD = 48;
const SWIPE_CLICK_WINDOW = 400;

export function AboutSlideshow() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const paused = hovered || open;
  const slide = SLIDES[current];
  // The six photos weigh about 700 kB together and the page's budget on a
  // phone is 400. Only the photo on screen and the one that follows it are
  // fetched; one already fetched keeps its address so going back never
  // flashes an empty frame.
  const [fetched, setFetched] = useState<ReadonlySet<number>>(() => new Set([0, 1]));

  useEffect(() => {
    const next = (current + 1) % SLIDES.length;
    setFetched((seen) =>
      seen.has(current) && seen.has(next) ? seen : new Set([...seen, current, next]),
    );
  }, [current]);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swipeAt = useRef(0);

  useEffect(() => {
    if (SLIDES.length < 2 || paused) return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;
    const timer = window.setTimeout(() => {
      setCurrent((i) => (i + 1) % SLIDES.length);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [paused, current]);

  const go = (delta: number) => {
    if (SLIDES.length < 2) return;
    setCurrent((i) => (i + delta + SLIDES.length) % SLIDES.length);
  };

  const handleTouchStart = (e: ReactTouchEvent) => {
    const touch = e.touches[0];
    if (touch) touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: ReactTouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    swipeAt.current = Date.now();
    go(dx < 0 ? 1 : -1);
  };

  const consumeSwipeClick = () => {
    if (Date.now() - swipeAt.current < SWIPE_CLICK_WINDOW) {
      swipeAt.current = 0;
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      } else if (e.key === 'ArrowLeft') {
        go(-1);
      } else if (e.key === 'ArrowRight') {
        go(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <div
      className="relative w-full h-full overflow-hidden touch-pan-y"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {SLIDES.map((photo, i) => (
        <img
          key={photo.src}
          src={fetched.has(i) ? photo.src : undefined}
          width={photo.width}
          height={photo.height}
          alt={t('landing.aboutImageLabel')}
          aria-hidden={i !== current}
          // All of them are lazy: the slideshow sits below the first viewport, and
          // the browser fetches the current slide when the band scrolls near.
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none select-none transition-opacity ease-in-out duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      <button
        type="button"
        onClick={() => {
          if (consumeSwipeClick()) return;
          setOpen(true);
        }}
        className="absolute inset-0 w-full h-full cursor-zoom-in"
        aria-label={t('common.fullPhoto')}
      />

      {SLIDES.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => {
              if (consumeSwipeClick()) return;
              go(-1);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-primary/40 hover:bg-primary/60 text-on-primary flex items-center justify-center transition-colors"
            aria-label={t('common.previousPhoto')}
          >
            <span className="material-symbols-outlined text-[24px]">chevron_left</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (consumeSwipeClick()) return;
              go(1);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-primary/40 hover:bg-primary/60 text-on-primary flex items-center justify-center transition-colors"
            aria-label={t('common.nextPhoto')}
          >
            <span className="material-symbols-outlined text-[24px]">chevron_right</span>
          </button>
        </>
      )}

      {open &&
        slide &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-primary/90 flex items-center justify-center p-4 select-none touch-pan-y"
            onClick={(e) => {
              e.stopPropagation();
              if (consumeSwipeClick()) return;
              setOpen(false);
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={slide.src}
              width={slide.width}
              height={slide.height}
              alt={t('landing.aboutImageLabel')}
              loading="lazy"
              className="max-w-full max-h-full object-contain rounded-xl select-none"
              onClick={(e) => e.stopPropagation()}
            />

            {SLIDES.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (consumeSwipeClick()) return;
                    go(-1);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-on-primary/10 hover:bg-on-primary/20 text-on-primary flex items-center justify-center transition-colors"
                  aria-label={t('common.previousPhoto')}
                >
                  <span className="material-symbols-outlined text-[24px]">chevron_left</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (consumeSwipeClick()) return;
                    go(1);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-on-primary/10 hover:bg-on-primary/20 text-on-primary flex items-center justify-center transition-colors"
                  aria-label={t('common.nextPhoto')}
                >
                  <span className="material-symbols-outlined text-[24px]">chevron_right</span>
                </button>
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-on-primary/10 text-on-primary font-title-md text-sm">
                  {current + 1} / {SLIDES.length}
                </span>
              </>
            )}

            <button
              type="button"
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-on-primary/10 hover:bg-on-primary/20 text-on-primary flex items-center justify-center transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              aria-label={t('common.close')}
            >
              <span className="material-symbols-outlined text-[24px]">close</span>
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
